import { computed, ref, type Ref } from 'vue';
import type { flightEntity } from 'freedom-shared';
import { useI18n } from 'vue-i18n';

import { api } from '@/shared/api';

import {
  aiChatApi as aiChatApiInstance,
  type PromptExample,
  type AiChatMessage,
  type AiChatSessionListItem,
  type StreamSuggestionItem,
  type TicketonItem,
  type TripPlan,
  type TripPlanDay,
} from '../api';
import {
  AI_CHAT_SESSION_STORAGE_KEY,
  MESSAGE_ASSISTANT,
  MESSAGE_USER,
} from '../config';

interface UseAiChatReturn {
  suggestions: Ref<StreamSuggestionItem[]>;
  messages: Ref<AiChatMessage[]>;
  isFetching: Ref<boolean>;
  isError: Ref<boolean>;
  isDisabled: Ref<boolean>;
  messageText: Ref<string>;
  isMessagesLoading: Ref<boolean>;
  fallbackSuggestions: Ref<PromptExample[] | undefined>;
  thinkingStatusText: Ref<string>;
  sessionId: Ref<string | null>;
  sessionHistory: Ref<AiChatSessionListItem[]>;
  isSessionSwitching: Ref<boolean>;
  switchingSessionId: Ref<string | null>;
  isSessionCreating: Ref<boolean>;
  isFallbackSuggestionsLoading: Ref<boolean>;
  fallbackSuggestionsError: Ref<unknown>;
  handleInput: (value: string) => void;
  handleSubmit: (callback: () => void) => Promise<void>;
  handleSuggestionClick: (suggestion: StreamSuggestionItem) => void;
  sendMessage: (prompt: string, callback: () => void) => Promise<void>;
  retryLastMessage: (callback: () => void) => Promise<void>;
  switchSession: (nextSessionId: string) => Promise<boolean>;
  createNewSession: () => Promise<boolean>;
  initializeChat: () => Promise<void>;
  loadMessageHistory: () => Promise<void>;
  refetchFallbackSuggestions: () => Promise<unknown>;
}

export const useAiChat = (): UseAiChatReturn => {
  const messages = ref<AiChatMessage[]>([]);
  const isFetching = ref(false);
  const isDisabled = ref(true);
  const messageText = ref('');
  // Индекс текущего "живого" текстового сообщения ассистента, в которое допечатываются чанки.
  const activeAssistantStreamMessageIndex = ref<number | null>(null);
  // Очередь символов для анимации "печати" ответа ассистента.
  const typingQueue = ref<string[]>([]);
  // Таймер, который по символу переносит данные из очереди в UI.
  const typingTimer = ref<ReturnType<typeof setInterval> | null>(null);
  // Флаг, что в текущем запросе уже приходили текстовые чанки.
  const hasStreamedAssistantText = ref(false);
  const thinkingStatusText = ref('');
  const isMessagesLoading = ref(false);
  const sessionId = ref<string | null>(null);
  const sessionHistory = ref<AiChatSessionListItem[]>([]);
  const isSessionSwitching = ref(false);
  const switchingSessionId = ref<string | null>(null);
  const isSessionCreating = ref(false);
  const isChatInitialized = ref(false);
  const initializePromise = ref<Promise<void> | null>(null);
  const fallbackSuggestions = ref<PromptExample[] | undefined>([]);
  const isFallbackSuggestionsLoading = ref(false);
  const fallbackSuggestionsError = ref<unknown>(null);
  const streamSuggestions = ref<StreamSuggestionItem[]>([]);
  const isError = ref(false);
  const lastSubmittedPrompt = ref('');
  const pendingProductsItems = ref<TicketonItem[] | null>(null);
  const hasTripPlanInCurrentResponse = ref(false);
  const tripPlanDraft = ref<TripPlan | null>(null);

  const aiChatApi = aiChatApiInstance(api.client);
  const { t } = useI18n();

  const loadSessionHistory = async () => {
    try {
      const sessions = await aiChatApi.getSessions();
      sessionHistory.value = sessions;
    } catch (error) {
      console.error('Error loading AI chat sessions:', error);
      sessionHistory.value = [];
    }
  };

  const refetchFallbackSuggestions = async () => {
    try {
      isFallbackSuggestionsLoading.value = true;
      fallbackSuggestionsError.value = null;
      const loadedPromptExamples = await aiChatApi.getPromptExamples(
        sessionId.value || undefined,
      );
      fallbackSuggestions.value = loadedPromptExamples;
      return loadedPromptExamples;
    } catch (error) {
      console.error('Error loading prompt examples:', error);
      fallbackSuggestionsError.value = error;
      fallbackSuggestions.value = [];
      return [];
    } finally {
      isFallbackSuggestionsLoading.value = false;
    }
  };

  const lastMessageSuggestions = computed<StreamSuggestionItem[]>(() => {
    for (let index = messages.value.length - 1; index >= 0; index -= 1) {
      const message = messages.value[index];
      if (message.content.type !== 'suggestions') {
        continue;
      }

      const normalizedMessageSuggestions = message.content.items.filter(
        suggestion => !!suggestion.text,
      );
      if (normalizedMessageSuggestions.length > 0) {
        return normalizedMessageSuggestions;
      }
    }

    return [];
  });

  const fallbackSuggestionsAsStreamSuggestions = computed<
    StreamSuggestionItem[]
  >(() => {
    const mappedFallbackSuggestions = fallbackSuggestions.value || [];
    return mappedFallbackSuggestions.map((suggestion, index) => {
      return {
        id: `fallback-${index}`,
        text: suggestion.prompt,
      };
    });
  });

  const suggestions = computed<StreamSuggestionItem[]>(() => {
    if (streamSuggestions.value.length > 0) {
      return streamSuggestions.value;
    }

    if (lastMessageSuggestions.value.length > 0) {
      return lastMessageSuggestions.value;
    }

    return fallbackSuggestionsAsStreamSuggestions.value;
  });

  const loadMessageHistory = async () => {
    if (!sessionId.value) {
      messages.value = [];
      return;
    }

    try {
      isMessagesLoading.value = true;
      const history = await aiChatApi.getMessages(sessionId.value);
      messages.value = history?.messages || [];
    } catch (error) {
      console.error('Error loading message history:', error);
    } finally {
      isMessagesLoading.value = false;
    }
  };

  const resetTypingState = () => {
    stopTypingAnimation();
    typingQueue.value = [];
  };

  const closeAssistantStreamMessage = () => {
    activeAssistantStreamMessageIndex.value = null;
  };

  const resetRuntimeStreamingState = () => {
    resetTypingState();
    closeAssistantStreamMessage();
    hasStreamedAssistantText.value = false;
    thinkingStatusText.value = '';
    isFetching.value = false;
  };

  const activateSession = async (nextSessionId: string) => {
    sessionId.value = nextSessionId;
    localStorage.setItem(AI_CHAT_SESSION_STORAGE_KEY, nextSessionId);

    resetRuntimeStreamingState();
    messages.value = [];
    messageText.value = '';
    isDisabled.value = true;
    isError.value = false;
    pendingProductsItems.value = null;
    hasTripPlanInCurrentResponse.value = false;
    tripPlanDraft.value = null;
    lastSubmittedPrompt.value = '';
    streamSuggestions.value = [];

    await refetchFallbackSuggestions();
    await loadMessageHistory();
    await loadSessionHistory();
  };

  // Останавливает анимацию печати, если она запущена.
  function stopTypingAnimation() {
    if (typingTimer.value) {
      clearInterval(typingTimer.value);
      typingTimer.value = null;
    }
  }

  // Возвращает активное текстовое сообщение ассистента, если оно есть.
  function getActiveAssistantTextMessage() {
    if (activeAssistantStreamMessageIndex.value === null) {
      return null;
    }

    const activeMessage =
      messages.value[activeAssistantStreamMessageIndex.value];
    if (!activeMessage || activeMessage.content.type !== 'text') {
      return null;
    }

    return activeMessage;
  }

  // Создает новое текстовое сообщение ассистента для стрима, если нужно.
  function ensureActiveAssistantTextMessage(initialText = '') {
    const existingMessage = getActiveAssistantTextMessage();
    if (existingMessage) {
      return existingMessage;
    }

    messages.value.push({
      content: {
        type: 'text',
        text: initialText,
      },
      sender: MESSAGE_ASSISTANT,
      created_at: new Date(),
    });
    activeAssistantStreamMessageIndex.value = messages.value.length - 1;

    return getActiveAssistantTextMessage();
  }

  // Добавляет текст в текущее активное сообщение ассистента.
  function appendToActiveAssistantText(textChunk: string) {
    if (!textChunk) {
      return;
    }

    const targetMessage = ensureActiveAssistantTextMessage();
    if (targetMessage?.content.type === 'text') {
      targetMessage.content.text += textChunk;
    }
  }

  // Дописывает в конец последнего ответа маркер, что генерация была прервана.
  function appendStreamAbortedMarker() {
    if (!hasStreamedAssistantText.value) {
      return;
    }

    const lastMessage = messages.value[messages.value.length - 1];
    if (
      lastMessage &&
      lastMessage.sender === MESSAGE_ASSISTANT &&
      lastMessage.content.type === 'text'
    ) {
      lastMessage.content.text += '\n[Генерация остановлена]';
      return;
    }

    messages.value.push({
      content: {
        type: 'text',
        text: '[Генерация остановлена]',
      },
      sender: MESSAGE_ASSISTANT,
      created_at: new Date(),
    });
  }

  // Запускает "печатание" чанков текста из очереди.
  function startTypingAnimation() {
    if (typingTimer.value) {
      return;
    }

    typingTimer.value = setInterval(() => {
      if (typingQueue.value.length === 0) {
        stopTypingAnimation();
        return;
      }

      const nextCharacter = typingQueue.value.shift();
      if (!nextCharacter) {
        return;
      }

      appendToActiveAssistantText(nextCharacter);
    }, 16);
  }

  // Мгновенно переносит все накопленные символы из очереди в сообщение.
  function flushTypingQueue() {
    if (typingQueue.value.length === 0) {
      return;
    }

    const queuedText = typingQueue.value.join('');
    typingQueue.value = [];
    stopTypingAnimation();

    if (!queuedText) {
      return;
    }

    appendToActiveAssistantText(queuedText);
  }

  function mergeTripPlanDay(days: TripPlanDay[], nextDay: TripPlanDay) {
    const existingDayIndex = days.findIndex(day => {
      return day.day_number === nextDay.day_number && day.date === nextDay.date;
    });
    if (existingDayIndex === -1) {
      return [...days, nextDay];
    }

    return days.map((day, index) => {
      if (index !== existingDayIndex) {
        return day;
      }

      return nextDay;
    });
  }

  const sendMessage = async (
    prompt: string,
    callback: () => void,
    appendUserMessage = true,
  ) => {
    if (!prompt.trim()) {
      return;
    }

    if (!sessionId.value) {
      console.error('Session ID is missing');
      return;
    }
    const currentSessionId = sessionId.value;

    if (appendUserMessage) {
      messages.value.push({
        content: {
          type: 'text',
          text: prompt,
        },
        sender: MESSAGE_USER,
        created_at: new Date(),
      });
    }

    lastSubmittedPrompt.value = prompt;

    isFetching.value = true;
    closeAssistantStreamMessage();
    resetTypingState();
    hasStreamedAssistantText.value = false;
    thinkingStatusText.value = '';
    isError.value = false;
    pendingProductsItems.value = null;
    hasTripPlanInCurrentResponse.value = false;
    tripPlanDraft.value = null;

    try {
      await aiChatApi.streamMessage(
        prompt,
        (eventType, data) => {
          isFetching.value = false;
          if (typeof callback === 'function') {
            callback();
          }

          if (
            eventType !== 'text_chunk' &&
            activeAssistantStreamMessageIndex.value !== null
          ) {
            flushTypingQueue();
            // Любой не-текстовый ивент завершает текущий "потоковый" текстовый блок.
            closeAssistantStreamMessage();
          }

          switch (eventType) {
            case 'text_chunk':
              if (data.message || data.text) {
                const messageChunk = data.message || data.text || '';

                hasStreamedAssistantText.value = true;
                // Первый текстовый чанк убирает статус "думаю" и запускает печать.
                thinkingStatusText.value = '';
                typingQueue.value.push(...Array.from(messageChunk));
                startTypingAnimation();
              }
              break;

            case 'status':
              if (data.message) {
                thinkingStatusText.value = data.message;
              }
              break;

            case 'offers':
              if (Array.isArray(data.offers) && data.offers.length > 0) {
                messages.value.push({
                  sender: MESSAGE_ASSISTANT,
                  content: {
                    type: 'offers',
                    search_id: data.search_id,
                    items: data.offers as flightEntity.types.MappedOffer[],
                  },
                  created_at: new Date(),
                });
              }
              break;

            case 'products':
              if (
                (data.kind === 'trip_plan' || data.type === 'trip_plan') &&
                data.plan
              ) {
                hasTripPlanInCurrentResponse.value = true;
                pendingProductsItems.value = null;
                messages.value.push({
                  sender: MESSAGE_ASSISTANT,
                  content: {
                    type: 'trip_plan',
                    plan: data.plan,
                  },
                  created_at: new Date(),
                });
                break;
              }

              if (Array.isArray(data.items) && data.items.length > 0) {
                pendingProductsItems.value = data.items;
              }
              break;

            case 'trip_plan':
              if (data.plan) {
                hasTripPlanInCurrentResponse.value = true;
                pendingProductsItems.value = null;
                tripPlanDraft.value = null;
                messages.value.push({
                  sender: MESSAGE_ASSISTANT,
                  content: {
                    type: 'trip_plan',
                    plan: data.plan,
                  },
                  created_at: new Date(),
                });
              }
              break;

            case 'trip_plan_header':
              tripPlanDraft.value = {
                title: data.title || '',
                subtitle: data.subtitle || '',
                has_ticketon: Boolean(data.has_ticketon),
                days: [],
                cover_image: data.cover_image || null,
                destination_iata: data.destination_iata || null,
                duration_days: data.duration_days || null,
                total_budget_kzt: data.total_budget_kzt || null,
              };
              break;

            case 'trip_plan_day':
              if (!data.day) {
                break;
              }

              if (!tripPlanDraft.value) {
                tripPlanDraft.value = {
                  title: '',
                  subtitle: '',
                  has_ticketon: false,
                  days: [],
                  cover_image: null,
                  destination_iata: null,
                  duration_days: null,
                  total_budget_kzt: null,
                };
              }

              tripPlanDraft.value.days = mergeTripPlanDay(
                tripPlanDraft.value.days,
                data.day,
              );
              break;

            case 'trip_plan_done': {
              const finalizedPlan = data.plan || tripPlanDraft.value;
              if (finalizedPlan) {
                hasTripPlanInCurrentResponse.value = true;
                pendingProductsItems.value = null;
                tripPlanDraft.value = null;
                messages.value.push({
                  sender: MESSAGE_ASSISTANT,
                  content: {
                    type: 'trip_plan',
                    plan: finalizedPlan,
                  },
                  created_at: new Date(),
                });
              }
              break;
            }

            case 'suggestions':
              if (
                Array.isArray(data.suggestions) &&
                data.suggestions.length > 0
              ) {
                streamSuggestions.value = [];
                streamSuggestions.value = data.suggestions.filter(
                  suggestion => !!suggestion.text,
                );
              }
              break;

            case 'ticketon':
              if (Array.isArray(data.items) && data.items.length > 0) {
                messages.value.push({
                  sender: MESSAGE_ASSISTANT,
                  content: {
                    type: 'ticketon',
                    items: data.items,
                  },
                  created_at: new Date(),
                });
              }
              break;

            case 'done':
              if (
                !hasTripPlanInCurrentResponse.value &&
                Array.isArray(pendingProductsItems.value) &&
                pendingProductsItems.value.length > 0
              ) {
                messages.value.push({
                  sender: MESSAGE_ASSISTANT,
                  content: {
                    type: 'ticketon',
                    items: pendingProductsItems.value,
                  },
                  created_at: new Date(),
                });
              }
              flushTypingQueue();
              resetTypingState();
              closeAssistantStreamMessage();
              hasStreamedAssistantText.value = false;
              pendingProductsItems.value = null;
              hasTripPlanInCurrentResponse.value = false;
              tripPlanDraft.value = null;
              thinkingStatusText.value = '';
              isFetching.value = false;
              break;

            case 'aborted':
              flushTypingQueue();
              resetTypingState();
              closeAssistantStreamMessage();
              appendStreamAbortedMarker();
              hasStreamedAssistantText.value = false;
              pendingProductsItems.value = null;
              hasTripPlanInCurrentResponse.value = false;
              tripPlanDraft.value = null;
              thinkingStatusText.value = '';
              isFetching.value = false;
              break;

            case 'error':
            default:
              console.error('Stream error:', data);
              if (isError.value) {
                return;
              }
              resetTypingState();
              messages.value.push({
                content: {
                  type: 'text',
                  text: t('promocode.defaultError'),
                },
                sender: MESSAGE_ASSISTANT,
                created_at: new Date(),
              });
              closeAssistantStreamMessage();
              hasStreamedAssistantText.value = false;
              pendingProductsItems.value = null;
              hasTripPlanInCurrentResponse.value = false;
              tripPlanDraft.value = null;
              thinkingStatusText.value = '';
              isFetching.value = false;
              isError.value = true;
              break;
          }
        },
        currentSessionId,
      );
    } catch (error) {
      resetTypingState();
      console.error('Error sending message:', error);
      messages.value.push({
        content: {
          type: 'text',
          text: t('promocode.defaultError'),
        },
        sender: MESSAGE_ASSISTANT,
        created_at: new Date(),
      });
      closeAssistantStreamMessage();
      hasStreamedAssistantText.value = false;
      thinkingStatusText.value = '';
      isFetching.value = false;
      isError.value = true;
    }
  };

  const handleInput = (value: string) => {
    messageText.value = value;
    isDisabled.value = value.trim().length === 0;
  };

  const handleSubmit = async (callback: () => void) => {
    const message = messageText.value.trim();
    if (!message) {
      return;
    }

    messageText.value = '';
    isDisabled.value = true;

    await sendMessage(message, callback);
  };

  const handleSuggestionClick = (suggestion: StreamSuggestionItem) => {
    messageText.value = suggestion.text || '';
    isDisabled.value = !messageText.value.trim();
  };

  const retryLastMessage = async (callback: () => void) => {
    if (!lastSubmittedPrompt.value.trim() || isFetching.value) {
      return;
    }

    await sendMessage(lastSubmittedPrompt.value, callback, false);
  };

  /**
   * Initialize chat by loading message history
   *
   * Prompt examples are loaded automatically by vue-query,
   * so we only need to manually load message history here.
   */
  const initializeChat = async () => {
    if (isChatInitialized.value) {
      return;
    }

    if (initializePromise.value) {
      await initializePromise.value;
      return;
    }

    initializePromise.value = (async () => {
      const storedSessionId = localStorage.getItem(AI_CHAT_SESSION_STORAGE_KEY);
      await loadSessionHistory();

      if (storedSessionId) {
        try {
          await aiChatApi.getSession(storedSessionId);
          await activateSession(storedSessionId);
        } catch (error) {
          console.error('Stored AI chat session is invalid:', error);
          localStorage.removeItem(AI_CHAT_SESSION_STORAGE_KEY);
        }
      } else {
        try {
          const createdSessionId = await aiChatApi.createSession();

          if (createdSessionId) {
            await activateSession(createdSessionId);
          }
        } catch (error) {
          console.error('Error creating AI chat session:', error);
        }
      }

      if (!sessionId.value) {
        try {
          const createdSessionId = await aiChatApi.createSession();
          if (createdSessionId) {
            await activateSession(createdSessionId);
          }
        } catch (error) {
          console.error('Error creating fallback AI chat session:', error);
        }
      }

      isChatInitialized.value = true;
    })();

    try {
      await initializePromise.value;
    } finally {
      initializePromise.value = null;
    }
  };

  const switchSession = async (nextSessionId: string): Promise<boolean> => {
    if (!nextSessionId) {
      return false;
    }

    if (nextSessionId === sessionId.value) {
      return true;
    }

    try {
      isSessionSwitching.value = true;
      switchingSessionId.value = nextSessionId;

      await aiChatApi.getSession(nextSessionId);
      await activateSession(nextSessionId);

      return true;
    } catch (error) {
      console.error('Error switching AI chat session:', error);
      return false;
    } finally {
      isSessionSwitching.value = false;
      switchingSessionId.value = null;
    }
  };

  const createNewSession = async (): Promise<boolean> => {
    try {
      isSessionCreating.value = true;
      const createdSessionId = await aiChatApi.createSession();

      if (!createdSessionId) {
        return false;
      }

      await activateSession(createdSessionId);
      return true;
    } catch (error) {
      console.error('Error creating new AI chat session:', error);
      return false;
    } finally {
      isSessionCreating.value = false;
    }
  };

  return {
    messages,
    isFetching,
    isError,
    isDisabled,
    messageText,
    isMessagesLoading,

    suggestions,
    fallbackSuggestions,
    thinkingStatusText,
    sessionId,
    sessionHistory,
    isSessionSwitching,
    switchingSessionId,
    isSessionCreating,
    isFallbackSuggestionsLoading,
    fallbackSuggestionsError,

    handleInput,
    handleSubmit,
    handleSuggestionClick,
    sendMessage,
    retryLastMessage,
    switchSession,
    createNewSession,
    initializeChat,
    loadMessageHistory,
    refetchFallbackSuggestions,
  };
};
